import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ExclusionAreaObservation } from '../../types/apiModels'
import { addComment } from '../../services/ruutsApi/exclusionAreaObservations/addComment'
import {
  createObservation,
  type NewExclusionAreaObservation,
} from '../../services/ruutsApi/exclusionAreaObservations/createObservation'
import { getByExclusionArea } from '../../services/ruutsApi/exclusionAreaObservations/getByExclusionArea'
import { getComments } from '../../services/ruutsApi/exclusionAreaObservations/getComments'
import { getUnresolvedCountByFarm } from '../../services/ruutsApi/exclusionAreaObservations/getUnresolvedCountByFarm'
import { resolveObservation } from '../../services/ruutsApi/exclusionAreaObservations/resolveObservation'
import { queryKeys } from './queryKeys'

type ObservationMutationScope = {
  exclusionAreaId: string | undefined
  farmId: string | undefined
}

// Creating or resolving an observation changes two independent server reads: the observation
// list of its exclusion area and the farm-wide unresolved badge counts. Neither response
// carries the other's state, so both keys are invalidated rather than seeded.
// The list key comes from the returned observation, not from the caller's scope: the modal can
// close (clearing the selected area) while the request is still in flight, and TanStack Query
// lets the running mutation adopt the latest options — invalidating the closed-over scope would
// then bust nothing and leave a resolved observation showing a live "Resolver" button.
const useObservationScopeInvalidation = ({ farmId }: Pick<ObservationMutationScope, 'farmId'>) => {
  const queryClient = useQueryClient()
  return (observation: ExclusionAreaObservation) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.exclusionAreaObservations(observation.exclusionAreaId),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.unresolvedObservationsCount(farmId),
    })
  }
}

export const useExclusionAreaObservations = ({
  exclusionAreaId,
}: {
  exclusionAreaId: string | undefined
}) =>
  useQuery({
    queryKey: queryKeys.exclusionAreaObservations(exclusionAreaId),
    queryFn: ({ signal }) =>
      getByExclusionArea({ exclusionAreaId: exclusionAreaId as string, signal }),
    enabled: !!exclusionAreaId,
  })

export const useUnresolvedObservationsCount = ({ farmId }: { farmId: string | undefined }) =>
  useQuery({
    queryKey: queryKeys.unresolvedObservationsCount(farmId),
    queryFn: ({ signal }) => getUnresolvedCountByFarm({ farmId: farmId as string, signal }),
    enabled: !!farmId,
  })

export const useCreateExclusionAreaObservation = ({
  exclusionAreaId,
  farmId,
}: ObservationMutationScope) => {
  const invalidateScope = useObservationScopeInvalidation({ farmId })
  return useMutation({
    mutationFn: (data: NewExclusionAreaObservation) =>
      createObservation({ exclusionAreaId: exclusionAreaId as string, data }),
    onSuccess: invalidateScope,
  })
}

export const useResolveExclusionAreaObservation = ({ farmId }: ObservationMutationScope) => {
  const invalidateScope = useObservationScopeInvalidation({ farmId })
  return useMutation({
    mutationFn: (observationId: string) => resolveObservation({ observationId }),
    onSuccess: invalidateScope,
  })
}

export const useExclusionAreaObservationComments = ({
  observationId,
}: {
  observationId: string | undefined
}) =>
  useQuery({
    queryKey: queryKeys.exclusionAreaObservationComments(observationId),
    queryFn: ({ signal }) => getComments({ observationId: observationId as string, signal }),
    enabled: !!observationId,
  })

export const useAddExclusionAreaObservationComment = ({
  observationId,
}: {
  observationId: string | undefined
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (message: string) =>
      addComment({ observationId: observationId as string, message }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.exclusionAreaObservationComments(observationId),
      }),
  })
}
